import { motion, useReducedMotion } from "framer-motion";
import {
  HousePlus,
  ShieldCheck,
  Network,
  ClipboardCheck,
  Smartphone,
  HardHat,
  ArrowRight,
  type LucideIcon,
} from "lucide-react";

interface Tile {
  Icon: LucideIcon;
  headline: string;
  body: string;
  gridClass: string;
  image?: string;
}

const tiles: Tile[] = [
  {
    Icon: HousePlus,
    headline: "NEU- & ALTBAUINSTALLATION",
    body: "Vom Rohbau bis zur Schlüsselübergabe — oder Modernisierung Ihrer Bestandsimmobilie. Wir bringen Ihre Installation auf den neuesten Stand.",
    gridClass: "lg:col-span-2 lg:row-span-2",
    image:
      "https://www.zweering-elektrotechnik.de/wp-content/uploads/2024/11/IMG_0143.jpeg",
  },
  {
    Icon: ShieldCheck,
    headline: "SICHERHEITSTECHNIK",
    body: "Videoüberwachung, Einbruchmeldeanlagen, Zutrittskontrolle. Damit Sie und Ihre Familie nachts ruhig schlafen.",
    gridClass: "lg:col-span-2 lg:row-span-1",
  },
  {
    Icon: Network,
    headline: "NETZWERK & WLAN",
    body: "Strukturierte Verkabelung, Access Points, Glasfaser-bereit.",
    gridClass: "lg:col-span-1 lg:row-span-1",
  },
  {
    Icon: ClipboardCheck,
    headline: "ZUSTANDS-CHECK",
    body: "E-Check, Messprotokolle, ehrliche Zweitmeinung zu bestehender Installation.",
    gridClass: "lg:col-span-1 lg:row-span-1",
  },
  {
    Icon: Smartphone,
    headline: "SMARTHOME-VERDRAHTUNG",
    body: "KNX, Loxone, Homematic — wir verdrahten Ihr Haus so, dass Sie heute oder in 10 Jahren intelligent werden können.",
    gridClass: "lg:col-span-2 lg:row-span-1",
  },
  {
    Icon: HardHat,
    headline: "BAUBEGLEITUNG IHRES PROJEKTS",
    body: "Sie planen, wir begleiten. Hilfestellung an der richtigen Stelle, ohne Ihnen das Projekt aus der Hand zu nehmen. Ideal für Bauherren mit Eigenleistungs-Anteil.",
    gridClass: "lg:col-span-2 lg:row-span-1",
  },
];

export default function HandwerkBento() {
  const reduce = useReducedMotion();

  return (
    <section
      className="bg-[#0A0A0A] px-6 md:px-10 lg:px-14"
      style={{ paddingTop: "clamp(80px, 10vw, 140px)", paddingBottom: "clamp(80px, 10vw, 140px)" }}
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 40 }}
          whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          style={{ marginBottom: "80px" }}
        >
          <p
            className="text-[#4CAF50] uppercase font-medium mb-5"
            style={{ fontSize: "0.8rem", letterSpacing: "0.18em" }}
          >
            DAS HANDWERK
          </p>
          <h2
            className="font-bold uppercase text-white leading-[0.95] break-words"
            style={{
              fontFamily: "Archivo, sans-serif",
              fontSize: "clamp(1.5rem, 5vw, 3.5rem)",
              letterSpacing: "clamp(-0.4px, -0.14vw, -2px)",
            }}
          >
            NICHT NUR ENERGIE —&nbsp;<br />
            AUCH JEDE STECKDOSE.
          </h2>
          <p
            className="mt-6 text-[#A0A0A0] text-base md:text-lg leading-relaxed"
            style={{ maxWidth: "600px" }}
          >
            Wir sind Elektrotechnik-Meisterbetrieb. Das heißt: Wir machen Ihr ganzes Haus — von der Grundinstallation bis zum letzten Schalter.
          </p>
        </motion.div>

        <div
          className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-5"
          style={{ gridAutoRows: "minmax(280px, auto)" }}
        >
          {tiles.map((tile, i) => (
            <motion.article
              key={tile.headline}
              initial={reduce ? false : { opacity: 0, y: 30 }}
              whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: i * 0.08 }}
              className={`group relative overflow-hidden flex flex-col ${tile.gridClass}`}
              style={{
                backgroundColor: "#141414",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: "16px",
                padding: "32px",
                minHeight: "280px",
                transition: "transform 300ms ease, border-color 300ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(76,175,80,0.4)";
                e.currentTarget.style.transform = "translateY(-4px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {tile.image && (
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage: `linear-gradient(rgba(10,10,10,0.85), rgba(10,10,10,0.6)), url(${tile.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    opacity: 0.55,
                  }}
                />
              )}

              <div className="relative flex-1 flex flex-col">
                <tile.Icon size={32} className="text-[#4CAF50] shrink-0" />

                <div className="mt-auto pt-8">
                  <h3
                    className="font-bold uppercase text-white leading-[1.1]"
                    style={{
                      fontFamily: "Archivo, sans-serif",
                      fontSize: "clamp(1.1rem, 1.6vw, 1.4rem)",
                      letterSpacing: "-0.4px",
                    }}
                  >
                    {tile.headline}
                  </h3>
                  <p
                    className="mt-3 text-[#A0A0A0] text-sm md:text-base leading-relaxed pr-10"
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {tile.body}
                  </p>
                </div>

                <ArrowRight
                  size={20}
                  className="absolute bottom-0 right-0 text-[#4CAF50]"
                  style={{ transition: "transform 300ms ease" }}
                  aria-hidden
                />
              </div>

              <style>{`
                .group:hover svg.lucide-arrow-right { transform: translateX(8px); }
              `}</style>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
